import { resolveMediaAccessDecision } from './mediaPlayerAccessGate';
import { releaseMediaLeaseIdBestEffort, stopMediaLeaseHeartbeat } from './mediaLeaseLifecycle';
import { runtimeOf } from './mediaPlayerProgress';
import { showToast } from './toastController';
import type { MountedPlayer } from './mediaPlayerTypes';
import type { Root } from './prototypeTypes';

const PREPARE_WINDOW_SECONDS = 45;
const MIN_HANDOFF_VALIDITY_SECONDS = 30;
const STORAGE_KEY = 'deepmox.audio.pending-resume.v1';
/** Single emergency switch: false restores the historical ended navigation path. */
export const AUDIO_HANDOFF_ENABLED = true;

function audioHandoffEnabled(root: Root): boolean {
	return AUDIO_HANDOFF_ENABLED && currentRuntime(root)?.audioBackgroundHandoffEnabled === true;
}

interface PreparedAudio {
	topicId: string;
	chapterIndex: number;
	response: Record<string, unknown>;
	url: string;
	expiresAt: number;
	leaseId?: string;
	heartbeatIntervalSeconds?: number;
	traceCode?: string;
	metadata?: { title: string; artist: string; album?: string };
}

interface PendingResume {
	topicId: string;
	chapterIndex: number;
	reason: string;
}

export interface AudioExplicitPlayIntent {
	generation: number;
	mediaGeneration: number;
	target: { topicId: string; chapterIndex: number } | null;
	requiresCancelledTarget: boolean;
}

/**
 * A single user-visible media action may cross several awaits (resume-card,
 * chapter lookup, token issuance).  Keep its ordering independent from the
 * audio handoff details so a later video action can invalidate an older audio
 * request before either response is allowed to mount.
 */
export interface MediaUserIntent {
	mediaGeneration: number;
}

interface HandoffState {
	topicId?: string;
	chapterIndex?: number;
	totalChapters?: number;
	chaptersLoading?: boolean;
	prepared?: PreparedAudio;
	preparedLoading?: boolean;
	/** Identity of the currently in-flight prefetch; stale finally must not clear it. */
	activePrepareRequestId?: number;
	prepareRequestId?: number;
	handoff?: { prepared: PreparedAudio; previousLeaseId?: string };
	pending?: PendingResume;
	needsUiSync?: boolean;
	lifecycleBound?: boolean;
	pendingNoticeShown?: boolean;
	generation?: number;
	mediaGeneration?: number;
	cancelled?: boolean;
	cancelledTarget?: { topicId: string; chapterIndex: number };
	explicitIntentTarget?: { topicId: string; chapterIndex: number };
	stalled?: boolean;
	transitionMedia?: MountedPlayer;
	chapterTitles?: Map<number, string>;
	topicTitle?: string;
}

const states = new WeakMap<object, HandoffState>();

function stateFor(root: Root): HandoffState {
	const key = root as unknown as object;
	let state = states.get(key);
	if (!state) {
		state = {};
		states.set(key, state);
	}
	return state;
}

function pageDocument(root: Root): Document {
	return root instanceof Document ? root : root.ownerDocument ?? document;
}

function currentRuntime(root: Root) {
	return runtimeOf(root);
}

/** Called after every explicit audio mount so stale prepared slots cannot leak. */
export function onAudioMounted(root: Root, media: MountedPlayer): void {
	const state = stateFor(root);
	state.generation = (state.generation ?? 0) + 1;
	if (state.prepared?.leaseId && state.prepared.leaseId !== media.playbackLeaseId) releaseMediaLeaseIdBestEffort(state.prepared.leaseId);
	if (state.handoff?.prepared.leaseId && state.handoff.prepared.leaseId !== media.playbackLeaseId) releaseMediaLeaseIdBestEffort(state.handoff.prepared.leaseId);
	if (state.handoff?.previousLeaseId && state.handoff.previousLeaseId !== media.playbackLeaseId) releaseMediaLeaseIdBestEffort(state.handoff.previousLeaseId);
	if (state.transitionMedia && state.transitionMedia !== media) stopMediaLeaseHeartbeat(state.transitionMedia);
	state.prepared = undefined;
	state.preparedLoading = false;
	state.activePrepareRequestId = undefined;
	state.handoff = undefined;
	state.pending = undefined;
	state.pendingNoticeShown = false;
	state.stalled = false;
	state.cancelled = false;
	state.cancelledTarget = undefined;
	state.explicitIntentTarget = undefined;
	bindLifecycle(root);
}

/** Media-native trigger only: no timer, rAF, DOM mutation, or UA branch. */
export function onAudioTimeupdate(root: Root, media: MountedPlayer): void {
	if (!audioHandoffEnabled(root) || isAudioHandoffTransitioning(root, media)) return;
	const runtime = currentRuntime(root);
	if (!runtime?.api || !media.topicId || !media.chapterIndex) return;
	const state = stateFor(root);
	if (state.handoff || state.preparedLoading || state.prepared) return;
	if (state.topicId !== media.topicId || state.chapterIndex !== media.chapterIndex) return;
	const duration = Number.isFinite(media.element.duration) && media.element.duration > 0 ? media.element.duration : media.fallbackDurationSeconds ?? 0;
	if (duration <= 0 || duration - media.element.currentTime > PREPARE_WINDOW_SECONDS) return;
	if (!state.totalChapters) {
		loadChapterCount(root, media);
		return;
	}
	const nextChapter = media.chapterIndex + 1;
	if (nextChapter > state.totalChapters) return;
	const plan = runtime.currentPlan;
	if (!plan) return;
	const access = resolveMediaAccessDecision(root, 'audio', media.topicId, nextChapter);
	if (!access.ok) return; // Free/paywall boundary: never expose a prepared URL.
	state.preparedLoading = true;
	const requestId = (state.prepareRequestId ?? 0) + 1;
	state.prepareRequestId = requestId;
	state.activePrepareRequestId = requestId;
	const generation = state.generation ?? 0;
	void runtime.api.audioPlaybackToken({ topicId: media.topicId, chapterIndex: nextChapter, plan, playbackRole: 'prepared' })
		.then((response) => {
			if (state.generation !== generation || state.topicId !== media.topicId || state.chapterIndex !== media.chapterIndex || state.handoff || state.cancelled) {
				releaseMediaLeaseIdBestEffort(readLeaseId(response));
				return;
			}
			const prepared = readPrepared(media.topicId!, nextChapter, response, state);
			if (!prepared) return;
			if (prepared.expiresAt <= Date.now() / 1000 + MIN_HANDOFF_VALIDITY_SECONDS) {
				releaseMediaLeaseIdBestEffort(prepared.leaseId);
				return;
			}
			replacePrepared(state, prepared);
		})
		.catch(() => undefined)
		.finally(() => {
			if (state.activePrepareRequestId !== requestId) return;
			state.preparedLoading = false;
			state.activePrepareRequestId = undefined;
		});
}

/**
 * The ended fast path. Returning true tells mediaPlayerCore not to release
 * the old lease, dispatch async legacy navigation, or clear Media Session.
 */
export function handoffPreparedAudioOnEnded(root: Root, media: MountedPlayer): boolean {
	if (!audioHandoffEnabled(root)) return false;
	const state = stateFor(root);
	// A media element that never reached `playing` has no active handoff
	// session. Preserve the existing foreground navigation behaviour for it.
	if (state.topicId !== media.topicId || state.chapterIndex !== media.chapterIndex) return false;
	// Unknown totals and terminal/paywall boundaries retain the established
	// foreground flow: it owns the final-state UI and access modal. Only a
	// known, allowed next chapter may become pending resume.
	if (!state.totalChapters || (media.chapterIndex ?? 0) >= state.totalChapters) return false;
	const nextAccess = resolveMediaAccessDecision(root, 'audio', media.topicId ?? '', (media.chapterIndex ?? 0) + 1);
	if (!nextAccess.ok) return false;
	const prepared = state.prepared;
	if (!prepared || prepared.topicId !== media.topicId || prepared.chapterIndex !== (media.chapterIndex ?? 0) + 1) {
		setPending(root, media, 'next_chapter_not_prepared');
		return true;
	}
	if (prepared.expiresAt <= Date.now() / 1000 + MIN_HANDOFF_VALIDITY_SECONDS) {
		releaseMediaLeaseIdBestEffort(prepared.leaseId);
		state.prepared = undefined;
		setPending(root, media, 'prepared_grant_expired');
		return true;
	}
	state.prepared = undefined;
	state.handoff = { prepared, previousLeaseId: media.playbackLeaseId };
	state.stalled = false;
	state.transitionMedia = media;
	state.generation = (state.generation ?? 0) + 1;
	stopMediaLeaseHeartbeat(media);
	media.suppressEndedMediaSessionCleanup = true;
	media.url = prepared.url;
	media.topicId = prepared.topicId;
	media.chapterIndex = prepared.chapterIndex;
	media.expiresAt = prepared.expiresAt;
	media.playbackLeaseId = prepared.leaseId;
	media.heartbeatIntervalSeconds = prepared.heartbeatIntervalSeconds;
	media.traceCode = prepared.traceCode;
	media.sessionMetadata = prepared.metadata;
	media.virtualCurrentTime = undefined;
	media.resumeApplied = false;
	media.lastProgressSyncAt = undefined;
	media.lastProgressPercent = undefined;
	media.fallbackDurationSeconds = audioDurationFor(root, prepared.topicId, prepared.chapterIndex);
	media.releaseStarted = false;
	media.engagementStartRequested = false;
	media.engagementStartLeaseId = undefined;
	media.engagementStartAttempts = undefined;
	if (media.engagementStartRetryTimer !== undefined) clearTimeout(media.engagementStartRetryTimer);
	media.engagementStartRetryTimer = undefined;
	media.claimOwnerOnNextPlaying = false;
	media.sourceGeneration = (media.sourceGeneration ?? 0) + 1;
	media.handoffSourceGeneration = media.sourceGeneration;
	media.handoffLoadStarted = false;
	// Suppress a duplicate old-source ended event until the new source emits
	// play/playing (mediaPlayerCore then re-arms this boundary normally).
	media.endBoundaryDispatched = true;
	// Auto-advance always starts the next chapter at 0:00: a stale cursor for
	// the target (e.g. it was half-played days ago) must not resurrect here.
	const runtime = runtimeOf(root);
	if (runtime) {
		const nextKey = `${prepared.topicId}:${prepared.chapterIndex}`;
		runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [nextKey]: 0 };
		runtime.resumeProgressByChapter = { ...(runtime.resumeProgressByChapter ?? {}), [nextKey]: 0 };
	}
	const beginPromotedSource = () => {
		try {
		media.element.src = prepared.url;
		media.element.load();
		const handoff = state.handoff;
		const handoffGeneration = state.generation;
		const handoffLeaseId = prepared.leaseId;
		const handoffUrl = prepared.url;
		void media.element.play().catch(() => {
			if (state.generation !== handoffGeneration || state.handoff !== handoff || state.transitionMedia !== media
				|| media.playbackLeaseId !== handoffLeaseId || media.url !== handoffUrl) return;
			setPending(root, media, 'handoff_play_rejected');
		});
		} catch {
		setPending(root, media, 'handoff_source_failed');
		}
	};
	const promote = currentRuntime(root)?.api?.promotePlaybackLease;
	if (!promote || !prepared.leaseId) {
		// Compatibility for the local no-API harness only. Deployed clients
		// always expose promotion; a rejected promotion below never loads media.
		beginPromotedSource();
		return true;
	}
	void promote.call(currentRuntime(root)?.api, prepared.leaseId).then(() => {
		if (state.handoff?.prepared !== prepared || state.transitionMedia !== media || state.cancelled) return;
		beginPromotedSource();
	}).catch(() => {
		if (state.handoff?.prepared !== prepared || state.transitionMedia !== media) return;
		// A prepared Range request is valid for preload, but playback ownership
		// has not moved until promotion succeeds. Stop immediately on failure.
		media.element.pause();
		releaseMediaLeaseIdBestEffort(prepared.leaseId);
		setPending(root, media, 'prepared_promotion_failed');
	});
	return true;
}

/** Returns true only for the handoff's first playing event. */
export function commitAudioHandoffOnPlaying(root: Root, media: MountedPlayer): boolean {
	const state = stateFor(root);
	const handoff = state.handoff;
	if (state.cancelled || !handoff || handoff.prepared.leaseId !== media.playbackLeaseId || handoff.prepared.url !== media.url) return false;
	state.handoff = undefined;
	state.transitionMedia = undefined;
	state.stalled = false;
	state.topicId = media.topicId;
	state.chapterIndex = media.chapterIndex;
	media.suppressEndedMediaSessionCleanup = false;
	if (handoff.previousLeaseId && handoff.previousLeaseId !== media.playbackLeaseId) releaseMediaLeaseIdBestEffort(handoff.previousLeaseId);
	const runtime = currentRuntime(root);
	if (runtime && media.topicId && media.chapterIndex) {
		if (pageDocument(root).visibilityState === 'hidden') state.needsUiSync = true;
		else syncUi(root, media.topicId, media.chapterIndex);
	}
	return true;
}

/** Establishes the active chapter before prepare starts for a normal/manual play. */
export function observeAudioPlaying(root: Root, media: MountedPlayer): boolean {
	if (stateFor(root).cancelled) return false;
	if (commitAudioHandoffOnPlaying(root, media)) return true;
	const state = stateFor(root);
	const active = (root as unknown as { __activeChapter?: { topicTitle?: unknown } }).__activeChapter;
	if (typeof active?.topicTitle === 'string' && active.topicTitle) state.topicTitle = active.topicTitle;
	if (state.topicId !== media.topicId || state.chapterIndex !== media.chapterIndex) {
		if (state.prepared?.leaseId) releaseMediaLeaseIdBestEffort(state.prepared.leaseId);
		state.topicId = media.topicId;
		state.chapterIndex = media.chapterIndex;
		state.totalChapters = undefined;
		state.prepared = undefined;
		state.preparedLoading = false;
		state.activePrepareRequestId = undefined;
		state.generation = (state.generation ?? 0) + 1;
	}
	loadChapterCount(root, media);
	return false;
}

export function isAudioHandoffTransitioning(root: Root, media: MountedPlayer): boolean {
	const handoff = stateFor(root).handoff;
	return Boolean(handoff && handoff.prepared.leaseId === media.playbackLeaseId && handoff.prepared.url === media.url
		&& media.handoffSourceGeneration === media.sourceGeneration);
}

export function isAudioHandoffCancelled(root: Root): boolean {
	return stateFor(root).cancelled === true;
}

export function cancelledAudioHandoffTarget(root: Root): { topicId: string; chapterIndex: number } | null {
	const state = stateFor(root);
	return state.cancelledTarget ?? null;
}

/** Every explicit play/resume invalidates an earlier in-flight fresh token. */
export function beginAudioHandoffExplicitPlayIntent(root: Root): AudioExplicitPlayIntent {
	const state = stateFor(root);
	state.generation = (state.generation ?? 0) + 1;
	const mediaGeneration = beginMediaUserIntent(root).mediaGeneration;
	const target = state.cancelledTarget ? { ...state.cancelledTarget } : null;
	state.explicitIntentTarget = target ?? undefined;
	return { generation: state.generation, mediaGeneration, target, requiresCancelledTarget: true };
}

/** Declares an explicit chapter-navigation request before its token is fetched. */
export function beginAudioHandoffNavigationIntent(root: Root, target: { topicId: string; chapterIndex: number }): AudioExplicitPlayIntent {
	const state = stateFor(root);
	state.generation = (state.generation ?? 0) + 1;
	const mediaGeneration = beginMediaUserIntent(root).mediaGeneration;
	state.explicitIntentTarget = { ...target };
	return { generation: state.generation, mediaGeneration, target: { ...target }, requiresCancelledTarget: false };
}

/** Invalidates older token work before a command must await chapter metadata. */
export function beginAudioHandoffCommandIntent(root: Root): AudioExplicitPlayIntent {
	const state = stateFor(root);
	state.generation = (state.generation ?? 0) + 1;
	const mediaGeneration = beginMediaUserIntent(root).mediaGeneration;
	state.explicitIntentTarget = undefined;
	return { generation: state.generation, mediaGeneration, target: null, requiresCancelledTarget: false };
}

/** Starts a user-directed media action which may later resolve to audio or video. */
export function beginMediaUserIntent(root: Root): MediaUserIntent {
	const state = stateFor(root);
	state.mediaGeneration = (state.mediaGeneration ?? 0) + 1;
	return { mediaGeneration: state.mediaGeneration };
}

export function isMediaUserIntentCurrent(root: Root, intent: MediaUserIntent): boolean {
	return stateFor(root).mediaGeneration === intent.mediaGeneration;
}

export function isAudioHandoffIntentCurrent(root: Root, intent: AudioExplicitPlayIntent): boolean {
	return stateFor(root).generation === intent.generation && isMediaUserIntentCurrent(root, intent);
}

/** Binds a resolved command target without minting a later intent generation. */
export function targetAudioHandoffIntent(root: Root, intent: AudioExplicitPlayIntent, target: { topicId: string; chapterIndex: number }): AudioExplicitPlayIntent | null {
	if (!isAudioHandoffIntentCurrent(root, intent)) return null;
	stateFor(root).explicitIntentTarget = { ...target };
	return { generation: intent.generation, mediaGeneration: intent.mediaGeneration, target: { ...target }, requiresCancelledTarget: false };
}

export function canCommitAudioHandoffExplicitPlay(root: Root, intent: AudioExplicitPlayIntent): boolean {
	const state = stateFor(root);
	if (!isMediaUserIntentCurrent(root, intent) || state.generation !== intent.generation || state.explicitIntentTarget?.topicId !== intent.target?.topicId
		|| state.explicitIntentTarget?.chapterIndex !== intent.target?.chapterIndex) return false;
	return !intent.requiresCancelledTarget || (state.cancelled === true
		&& state.cancelledTarget?.topicId === intent.target?.topicId
		&& state.cancelledTarget?.chapterIndex === intent.target?.chapterIndex);
}

/** Unmount/navigation invalidates any delayed fresh-token response. */
export function invalidateAudioHandoffIntent(root: Root): void {
	const state = stateFor(root);
	state.generation = (state.generation ?? 0) + 1;
	beginMediaUserIntent(root);
	state.explicitIntentTarget = undefined;
}

/** Clears cancellation only after a fresh explicit token has replaced the revoked source. */
export function resetAudioHandoffCancellation(root: Root): boolean {
	const state = stateFor(root);
	if (!state.cancelled) return false;
	state.cancelled = false;
	state.cancelledTarget = undefined;
	state.pending = undefined;
	state.pendingNoticeShown = false;
	return true;
}

export function markAudioHandoffStalled(root: Root, media: MountedPlayer): void {
	if (isAudioHandoffTransitioning(root, media) && media.handoffLoadStarted) stateFor(root).stalled = true;
}

/** `abort` from the old source is expected before the replacement emits loadstart. */
export function onAudioHandoffLoadStart(root: Root, media: MountedPlayer): void {
	if (isAudioHandoffTransitioning(root, media)) {
		media.handoffLoadStarted = true;
	}
}

export function canFailAudioHandoff(root: Root, media: MountedPlayer): boolean {
	return isAudioHandoffTransitioning(root, media) && media.handoffLoadStarted === true;
}

/** Called by core for error/abort/stalled before the new source reaches playing. */
export function failAudioHandoff(root: Root, media: MountedPlayer, reason: string): boolean {
	if (!isAudioHandoffTransitioning(root, media)) return false;
	setPending(root, media, reason);
	media.mediaSessionCleanup?.();
	media.mediaSessionCleanup = undefined;
	return true;
}

/** A remote explicit playback claim wins even during an ended → playing gap. */
export function cancelAudioHandoffForRemoteClaim(root: Root, media: MountedPlayer | undefined): void {
	const state = stateFor(root);
	// A prepared next chapter is speculative while the current source is still
	// playing. Only an already-started ended→playing handoff resumes its next
	// target; otherwise resume the current chapter with a fresh grant.
	const cancelledTarget = state.handoff?.prepared;
	if (cancelledTarget) state.cancelledTarget = { topicId: cancelledTarget.topicId, chapterIndex: cancelledTarget.chapterIndex };
	else if (media?.topicId && media.chapterIndex) state.cancelledTarget = { topicId: media.topicId, chapterIndex: media.chapterIndex };
	state.cancelled = true;
	state.generation = (state.generation ?? 0) + 1;
	beginMediaUserIntent(root);
	if (state.prepared?.leaseId) releaseMediaLeaseIdBestEffort(state.prepared.leaseId);
	if (state.handoff?.prepared.leaseId) releaseMediaLeaseIdBestEffort(state.handoff.prepared.leaseId);
	if (state.handoff?.previousLeaseId) releaseMediaLeaseIdBestEffort(state.handoff.previousLeaseId);
	state.prepared = undefined;
	state.handoff = undefined;
	state.transitionMedia = undefined;
	state.preparedLoading = false;
	state.activePrepareRequestId = undefined;
	state.stalled = false;
	if (media) {
		stopMediaLeaseHeartbeat(media);
		releaseMediaLeaseIdBestEffort(media.playbackLeaseId);
		media.element.pause();
		// Keep the system play action alive: its callback routes through the
		// explicit fresh-token path, so it cannot revive this released URL.
	}
}

export function takePendingAudioResume(root: Root): PendingResume | null {
	const state = stateFor(root);
	const pending = state.pending ?? readPersistedPending();
	state.pending = undefined;
	state.pendingNoticeShown = false;
	clearPersistedPending();
	return pending ?? null;
}

function loadChapterCount(root: Root, media: MountedPlayer): void {
	const state = stateFor(root);
	if (state.chaptersLoading) return;
	const api = currentRuntime(root)?.api;
	if (!api || !media.topicId) return;
	state.chaptersLoading = true;
	void api.listTopicChapters(media.topicId)
		.then((result) => {
			if (state.topicId !== media.topicId || state.chapterIndex !== media.chapterIndex) return;
			const chapters = Array.isArray(result?.chapters) ? result.chapters : [];
			state.totalChapters = chapters.length;
			state.chapterTitles = new Map(chapters.map((chapter: { index?: unknown; title?: unknown }) => [Number(chapter.index), typeof chapter.title === 'string' ? chapter.title : '']));
		})
		.catch(() => undefined)
		.finally(() => { state.chaptersLoading = false; });
}

function readPrepared(topicId: string, chapterIndex: number, response: unknown, state: HandoffState): PreparedAudio | null {
	if (!response || typeof response !== 'object') return null;
	const value = response as Record<string, unknown>;
	if (value.tokenType !== 'audio' || typeof value.signedPlaybackUrl !== 'string' || !value.signedPlaybackUrl) return null;
	const expires = typeof value.expiresInSeconds === 'number' ? value.expiresInSeconds : 0;
	return {
		topicId,
		chapterIndex,
		response: value,
		url: value.signedPlaybackUrl,
		expiresAt: Date.now() / 1000 + expires,
		leaseId: readLeaseId(value),
		heartbeatIntervalSeconds: typeof value.heartbeatIntervalSeconds === 'number' ? value.heartbeatIntervalSeconds : undefined,
		traceCode: typeof (value.watermark as { traceCode?: unknown } | undefined)?.traceCode === 'string' ? (value.watermark as { traceCode: string }).traceCode : undefined,
		metadata: { title: state.chapterTitles?.get(chapterIndex) || `Chapter ${chapterIndex}`, artist: state.topicTitle || 'DeepMox', album: 'DeepMox' },
	};
}

function readLeaseId(response: unknown): string | undefined {
	const value = response as { playbackLeaseId?: unknown } | null;
	return typeof value?.playbackLeaseId === 'string' ? value.playbackLeaseId : undefined;
}

function replacePrepared(state: HandoffState, prepared: PreparedAudio): void {
	if (state.prepared?.leaseId && state.prepared.leaseId !== prepared.leaseId) releaseMediaLeaseIdBestEffort(state.prepared.leaseId);
	state.prepared = prepared;
}

/** Test-only seam for the otherwise impossible-to-reach overlapping response race. */
export const __audioHandoffTestHooks = {
	replacePrepared(root: Root, leaseId: string): void {
		replacePrepared(stateFor(root), {
			topicId: 'test-topic', chapterIndex: 1, response: {}, url: `https://audio.test/${leaseId}`,
			expiresAt: Date.now() / 1000 + 300, leaseId,
		});
	},
};

function setPending(root: Root, media: MountedPlayer, reason: string): void {
	const state = stateFor(root);
	const failedHandoff = state.handoff;
	const target = failedHandoff?.prepared ?? state.prepared;
	if (target) releaseMediaLeaseIdBestEffort(target.leaseId);
	if (failedHandoff?.previousLeaseId && failedHandoff.previousLeaseId !== target?.leaseId) releaseMediaLeaseIdBestEffort(failedHandoff.previousLeaseId);
	if (!failedHandoff) {
		stopMediaLeaseHeartbeat(media);
		releaseMediaLeaseIdBestEffort(media.playbackLeaseId);
	}
	state.generation = (state.generation ?? 0) + 1;
	state.prepared = undefined;
	state.preparedLoading = false;
	state.activePrepareRequestId = undefined;
	state.handoff = undefined;
	state.stalled = false;
	media.suppressEndedMediaSessionCleanup = false;
	const pendingTarget = failedHandoff?.prepared ?? target;
	const pending: PendingResume = {
		topicId: pendingTarget?.topicId ?? media.topicId ?? '',
		chapterIndex: pendingTarget?.chapterIndex ?? (media.chapterIndex ?? 0) + 1,
		reason,
	};
	if (!pending.topicId || pending.chapterIndex < 1) return;
	state.pending = pending;
	persistPending(pending);
	if (pageDocument(root).visibilityState !== 'hidden') presentPendingResume(root, state);
}

function bindLifecycle(root: Root): void {
	const state = stateFor(root);
	if (state.lifecycleBound || typeof window === 'undefined') return;
	state.lifecycleBound = true;
	const resume = () => {
		const doc = pageDocument(root) as Document & { wasDiscarded?: boolean };
		if (doc.visibilityState === 'hidden') return;
		if (state.needsUiSync && state.topicId && state.chapterIndex) {
			state.needsUiSync = false;
			syncUi(root, state.topicId, state.chapterIndex);
		}
		if (doc.wasDiscarded) {
			releaseMediaLeaseIdBestEffort(state.prepared?.leaseId);
			state.prepared = undefined;
		}
		if (state.stalled && state.transitionMedia && state.handoff) {
			state.stalled = false;
			failAudioHandoff(root, state.transitionMedia, 'handoff_stalled_after_resume');
		}
		if (state.pending ?? readPersistedPending()) presentPendingResume(root, state);
	};
	const doc = pageDocument(root);
	doc.addEventListener('visibilitychange', resume, true);
	// Chromium's Page Lifecycle freeze/resume events target Document. They do
	// not bubble to Window, so bind them here (capture also covers vendor event
	// dispatch quirks) before restoring a stalled handoff UI.
	doc.addEventListener('resume', resume, true);
	doc.addEventListener('freeze', () => undefined, true);
	window.addEventListener('pageshow', resume);
	window.addEventListener('pagehide', (event) => {
		if (!(event as PageTransitionEvent).persisted) releaseMediaLeaseIdBestEffort(state.prepared?.leaseId);
	});
}

function audioDurationFor(root: Root, topicId: string, chapterIndex: number): number | undefined {
	const seconds = runtimeOf(root)?.audioSecondsByChapter?.[`${topicId}:${chapterIndex}`]
		?? runtimeOf(root)?.audioSecondsByChapter?.[String(chapterIndex)];
	return typeof seconds === 'number' && seconds > 0 ? seconds : undefined;
}

function presentPendingResume(root: Root, state: HandoffState): void {
	if (state.pendingNoticeShown) return;
	state.pendingNoticeShown = true;
	showToast({
		type: 'warning',
		message: 'Next chapter is ready to resume.',
		durationMs: 0,
		actionLabel: 'Resume audio',
		onAction: () => (root as Document | HTMLElement).dispatchEvent(new CustomEvent('deepmox:audio-resume-request')),
	});
}

function syncUi(root: Root, topicId: string, chapterIndex: number): void {
	(root as Document | HTMLElement).dispatchEvent(new CustomEvent('deepmox:audio-handoff-ui', {
		detail: { topicId, chapterIndex },
	}));
}

function persistPending(pending: PendingResume): void {
	try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending)); } catch { /* private storage may be unavailable */ }
}

function readPersistedPending(): PendingResume | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const value = JSON.parse(raw) as Partial<PendingResume>;
		return typeof value.topicId === 'string' && Number.isInteger(value.chapterIndex) && value.chapterIndex! > 0 && typeof value.reason === 'string'
			? value as PendingResume : null;
	} catch { return null; }
}

function clearPersistedPending(): void {
	try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
